// Proof-type helpers, shared between the portal UI, the quote-update service
// (which persists the resolved quote-level intent onto Quote.proof_type), and
// the pricing-request builder (which decides whether to emit the quote-level
// physical_proof project).
//
// proof_type is authored PER LINE as a schema field (config.proof_type), but a
// physical proof is a single QUOTE-LEVEL Pattern B Service. resolveQuoteProofType
// reduces all of a quote's line configs to ONE intent with the precedence
// physical > digital > none: if ANY project asks for a physical proof, the
// whole quote gets one (and one physical_proof Service).
//
// Since the configurator's proof_type became a MULTI-SELECT (a line can ask for
// both a digital AND a physical proof), config.proof_type is now a string[].
// readLineProofType reduces a single line's array to its dominant intent (same
// physical > digital > none precedence) so the quote-level reduction is
// unchanged. Legacy quotes that stored a single string are still read correctly.
//
// IMPORTANT: reducing per-line picks to a quote-level intent is intent
// aggregation, NOT pricing — the charge (the proof's own print + shipping) is
// computed and owned by Nexus. See CLAUDE.md §"Pricing logic lives in the ERP"
// and vocabulary.ts PHYSICAL_PROOF_FAMILY_KEY.
import { PROOF_TYPES } from '../vocabulary.js';
import { isConfiguredLine, lineIsProofed } from './product-type.js';
const PROOF_TYPE_SET = new Set(PROOF_TYPES);
// Read a single line's proof_type from its `config` blob, reduced to ONE
// dominant intent (physical > digital > none). Returns null when the line
// carries no (valid) proof_type — treated as "no opinion", which contributes
// nothing to the quote-level resolution. Handles BOTH the current multi-select
// shape (string[], e.g. ['digital', 'physical']) and the legacy single string.
//
// Also null on a line whose family is not asked the question, even when a stale
// proof_type is still stored on it: the form hides the field there and the
// backend does not scrub hidden fields on save. That keeps a quote whose only
// media is plain, undecorated drives at 'none' rather than 'digital', and off
// the physical_proof line (FB-0094).
export function readLineProofType(config) {
    if (typeof config !== 'object' || config === null)
        return null;
    if (isConfiguredLine(config) && !lineIsProofed(config))
        return null;
    const raw = config['proof_type'];
    if (Array.isArray(raw)) {
        // A line can hold both digital + physical; physical dominates because it's
        // the only paid, quote-level Pattern B Service.
        if (raw.includes('physical'))
            return 'physical';
        if (raw.includes('digital'))
            return 'digital';
        if (raw.includes('none'))
            return 'none';
        return null;
    }
    return typeof raw === 'string' && PROOF_TYPE_SET.has(raw) ? raw : null;
}
// Reduce every line's proof_type to ONE quote-level intent.
// Precedence: physical > digital > none. A quote where no line expresses a
// proof_type resolves to 'none'.
export function resolveQuoteProofType(configs) {
    let sawDigital = false;
    for (const config of configs) {
        const p = readLineProofType(config);
        if (p === 'physical')
            return 'physical';
        if (p === 'digital')
            sawDigital = true;
    }
    return sawDigital ? 'digital' : 'none';
}
//# sourceMappingURL=proof.js.map